import { BlankSlate } from "@msuite/ui/blank-slate";
import { Button } from "@msuite/ui/button";
import { useTranslation } from "@msuite/ui/localization";
import { Modal, type ModalProps } from "@msuite/ui/modal";
import { useQuery } from "@msuite/ui/query";
import { responseHandler } from "@msuite/ui/utils";
import { moment } from "@msuite/utils/date";
import type { ProjectGallery } from "@msuite/utils/types";
import { useState } from "react";
import { client } from "@/hono";
import { PickProjectGalleryFilesItem } from "./pick-project-gallery-files-item";

/** Props Interface */
interface PickProjectGalleryFilesModalProps extends ModalProps {
	projectId: string;
	excludeFileIds?: string[];
	onComplete: (fileIds: string[]) => Promise<void> | void;
}

export function PickProjectGalleryFilesModal({
	isOpen,
	onClose,
	projectId,
	excludeFileIds,
	onComplete,
}: PickProjectGalleryFilesModalProps) {
	/** Hooks */
	const { t } = useTranslation();

	/** State */
	const [selectedFileIds, setSelectedFileIds] = useState<string[]>([]);

	/** Query */
	const { data: gallery, isLoading } = useQuery({
		queryKey: ["projects", "gallery", projectId],
		queryFn: async () => {
			const response = await client.projects[":projectId"].gallery.$get({
				param: { projectId },
			});
			const data = await responseHandler(response);
			return data.gallery;
		},
	});

	/** Computed */
	const dates = deduplicateGalleryDates(gallery, excludeFileIds);

	/** Functions */
	function handleOnToggle(fileId: string) {
		setSelectedFileIds((ids) =>
			ids.includes(fileId) ? ids.filter((id) => id !== fileId) : [...ids, fileId],
		);
	}

	async function handleOnConfirm() {
		await onComplete(selectedFileIds);
		onClose();
	}

	/** Render */
	return (
		<Modal
			isOpen={isOpen}
			onClose={onClose}
			title={t("general.select_existing_files")}
			width="700px"
			data-cy="pick-project-gallery-files-modal"
		>
			<Modal.Body>
				<div className="v-stack gap-4 max-h-[60vh] overflow-y-auto">
					{!dates?.length && <BlankSlate isLoading={isLoading} />}
					{dates?.map((dateItem) => (
						<div
							className="v-stack gap-2"
							key={dateItem.date}
						>
							<p className="font-semibold text-sm">{moment(dateItem.date).dateFormat()}</p>
							<div className="h-stack flex-wrap gap-2">
								{dateItem.items.map((item) => (
									<PickProjectGalleryFilesItem
										key={item.id}
										item={item}
										isSelected={selectedFileIds.includes(item.id)}
										onToggle={handleOnToggle}
									/>
								))}
							</div>
						</div>
					))}
				</div>
			</Modal.Body>
			<Modal.Footer>
				<div className="self-end h-stack gap-4 ml-auto">
					<Button
						variant="ghost"
						onClick={onClose}
					>
						{t("general.cancel")}
					</Button>
					<Button
						variant="primary"
						isDisabled={selectedFileIds.length < 1}
						onClick={handleOnConfirm}
						data-cy="pick-project-gallery-files-confirm-button"
					>
						{t("general.select")}
					</Button>
				</div>
			</Modal.Footer>
		</Modal>
	);
}

/**
 * A file can be linked to multiple tickets/notes, which means the same gallery item can be
 * returned more than once — collapse those down to a single, first occurrence per file id.
 */
function deduplicateGalleryDates(gallery: ProjectGallery | undefined, excludeFileIds?: string[]) {
	if (!gallery) return gallery;
	const seenFileIds = new Set<string>();
	return gallery
		.map((dateItem) => ({
			...dateItem,
			items: dateItem.items.filter((item) => {
				if (excludeFileIds?.includes(item.id)) return false;
				if (seenFileIds.has(item.id)) return false;
				seenFileIds.add(item.id);
				return true;
			}),
		}))
		.filter((dateItem) => dateItem.items.length > 0);
}
